import RichText from "@src/components/elements/RichText";
import clsx from "clsx";
import { graphql } from "gatsby";
import React from "react";
import { getContentfulPreviewPropsFn } from "@utils";
import { useLiveUpdates } from "@src/hooks/useLiveUpdates";

function ChevronIcon({ open, className, ...rest }) {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={clsx(
        className,
        !open
          ? "rotate-180 transition-transform"
          : "rotate-0 transition-transform"
      )}
      {...rest}
    >
      <path
        d="M5 15L12 8L19 15"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function AccordionItem({theme, isOpen, entry: rawEntry, setOpen = (id) => {}, isLast = false}) {
  const entry = useLiveUpdates(rawEntry);
  const body = !entry.body?.json
  ? entry.body
  : { raw: JSON.stringify(entry.body?.json) };
  const contentful_id = rawEntry.contentful_id;
  const title = entry.title;
  const getPreviewProps = getContentfulPreviewPropsFn(contentful_id);

  return (
    <div
      id={`accordion-collapse-${contentful_id}`}
      data-accordion="collapse"
      className={clsx(
        "border border-base text-wi border-b-0 border-x-0 0 p-5",
        isLast && "pb-0",
      )}
    >
      <h3 id={`accordion-collapse-heading-${contentful_id}`} className="plain">
        <button
          type="button"
          className={clsx(
            "flex items-center gap-3 justify-between w-full",
            "font-bold rtl:text-right text-18px",
            "transition-transform ease-in-out duration-250",
          )}
          data-accordion-target={`accordion-collapse-body-${contentful_id}`}
          aria-expanded={isOpen ? "true" : "false"}
          aria-controls={`accordion-collapse-body-${contentful_id}`}
          onClick={() => setOpen?.(contentful_id)}
        >
          <div className="text-left" {...getPreviewProps("title")}>{title}</div>
          <ChevronIcon
            open={isOpen}
            className={clsx(
              "flex-none",
            )}
          />
        </button>
      </h3>
      <div
        id={`accordion-collapse-body-${contentful_id}`}
        className={clsx(
          "grid transition-grid-rows duration-300 ease-in-out m-0",
          isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
        )}
        aria-labelledby={`accordion-collapse-heading-${contentful_id}`}
      >
        <div
          className={clsx(
            "overflow-hidden",
          )}
        >
          <div className="pt-3" {...getPreviewProps("body")}>
            {body?.raw && <RichText bodyRichText={body} classes="space-y-4" />}
          </div>
        </div>
      </div>
    </div>
  );
}

export const AccordionItemFragment = graphql`
  fragment AccordionItem on ContentfulAccordionItem {
    __typename
    contentful_id
    internalName
    title
    isOpen
    body {
      raw
      references {
        ...GatsbyImageCommon
        gatsbyImageData(placeholder: BLURRED, quality: 90)
      }
    }
  }
`;
